import React, { useEffect, useState } from "react";
import * as S from "./styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Grid,
  Modal,
  TablePagination,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { MdOutlineCopyAll, MdOutlineGroups } from "react-icons/md";
import FutProgress from "@/components/FutProgress";
import { AiFillStar, AiFillTrophy } from "react-icons/ai";
import { FaFlagCheckered } from "react-icons/fa";
import {
  AreaChart,
  ResponsiveContainer,
  Area,
  CartesianGrid,
  Tooltip,
  YAxis,
  XAxis,
} from "recharts";

import FutDatePicker from "@/components/FutDatePicker";
import { useForm } from "react-hook-form";
import FutSelect from "@/components/FutSelect";
import { affiliatedLevelsData } from "./data";
import { FutButton } from "@/components/FutButton";
import { BsChevronDown } from "react-icons/bs";
import { BiFilterAlt, BiShareAlt } from "react-icons/bi";
import { useAuth } from "@/context/AuthContext";

import { toast } from "react-toastify";
import { api } from "@/services/api";
import { CountUserChildren, User, UserAff } from "@/interfaces/user";
import { catchError } from "@/utils/catchError";
import * as dateFns from "date-fns";
import { UseUser } from "@/context/UserContext";
import { useTranslation } from "react-i18next";

interface IFutAffiliated {
  datePicker: Date | null;
  selectAff: number;
}

interface IAffStats {
  name: string;
  reais: number;
}

type AffiliatedResponse = {
  affiliates: Array<UserAff & { level: number }>;
  countChildren: number;
};

interface IGraphData {
  [key: number]: {
    [key: number]: {
      [key: number]: number;
    };
  };
}

export default function Affiliated() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMedia900 = !useMediaQuery(theme.breakpoints.up("md"));
  const isMedia600 = !useMediaQuery(theme.breakpoints.up("sm"));

  const { decodedToken } = useAuth();
  const { refreshBalance } = UseUser();


  const [affiliates, setAffiliates] = useState<AffiliatedResponse>({
    affiliates: [],
    countChildren: 0,
  });
  const [countAffiliates, setCountAffiliates] = useState<CountUserChildren>();
  const [loadingGraph, setLoadingGraph] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 20 });

  const [affStats, setAffStats] = useState<IAffStats[]>([]);

  const [openMobileModal, setOpenMobileModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countLoading, setCountLoading] = useState(false);

  const AffStatsForm = useForm({
    defaultValues: {
      dateStats: new Date(),
    },
  });

  const affiliatedForm = useForm<IFutAffiliated>({
    defaultValues: {
      datePicker: null,
      selectAff: affiliatedLevelsData[0].value,
    },
  });

  const getAffiliates = async (filterDate?: string, resetPage?: boolean) => {
    try {
      setLoading(true);
      const level = affiliatedForm.getValues("selectAff");
      const date = affiliatedForm.getValues("datePicker");
      const convertedDate = date ? dateFns.format(date, "yyyy-MM-dd") : undefined

      const responseAff = await api.get(`/user/getChild/${decodedToken.id}`, {
        params: {
          filterDate: filterDate || convertedDate,
          level,
          page: resetPage ? 0 : pagination.page,
          pageSize: pagination.pageSize,
        },
      });

      if (responseAff.data && responseAff.data.body) {
        const affiliatesApi: AffiliatedResponse = responseAff.data.body;
        const safeAffiliatesApi = {
          ...affiliatesApi,
          affiliates: affiliatesApi.affiliates || [],
          countChildren: affiliatesApi.countChildren || 0
        };
        setAffiliates(safeAffiliatesApi);
      } else {
        setAffiliates({
          affiliates: [],
          countChildren: 0
        });
      }
    } catch (e) {
      setAffiliates({
        affiliates: [],
        countChildren: 0
      });

      const message = catchError(e);
      if (message) {
        toast.error(message);
        return;
      }
      toast.error(
        "Ocorreu um erro inesperado ao buscar o histórico de afiliado"
      );
    } finally {
      setLoading(false);
    }
  };

  const getGraphData = async () => {
    try {
      setLoadingGraph(true);
      const dateStats = AffStatsForm.getValues("dateStats");

      const response = await api.get(`/user/getGraph/${decodedToken.id}`, {
        params: {
          filterYearMonth: dateFns.format(dateStats, "yyyy-MM"),
        },
      });

      if (response.data && response.data.body) {
        const graphData: IGraphData = response.data.body;

        const monthSales =
          graphData[dateStats.getFullYear()]?.[dateStats.getMonth() + 1];

        if (monthSales) {
          const organizedData = Object.keys(monthSales).map((key) => {
            return { name: key, reais: monthSales[key as any] || 0 };
          });
          setAffStats(organizedData);
          setLoadingGraph(false);
          return;
        }
      }

      // Se não tem dados ou ocorreu algum problema, inicializa com array vazio
      setAffStats([]);
      setLoadingGraph(false);
    } catch (e) {
      // Em caso de erro, inicializa com array vazio
      setAffStats([]);
      setLoadingGraph(false);
      toast.error("Ocorreu um erro ao carregar o gráfico de vendas");
    }
  };

  const getCountAffiliates = async () => {
    try {
      setCountLoading(true);
      const response = await api.get(
        `/user/countChildUsers/${decodedToken.id}`
      );

      if (response.data && response.data.body) {
        setCountAffiliates(response.data.body);
      } else {
        setCountAffiliates({
          child_lvl_1: { count: 0, total_earned: 0, payment_pending: 0 },
          child_lvl_2: { count: 0, total_earned: 0, payment_pending: 0 },
          child_lvl_3: { count: 0, total_earned: 0, payment_pending: 0 },
          child_lvl_4: { count: 0, total_earned: 0, payment_pending: 0 }
        });
      }
      setCountLoading(false);
    } catch (e) {
      setCountAffiliates({
        child_lvl_1: { count: 0, total_earned: 0, payment_pending: 0 },
        child_lvl_2: { count: 0, total_earned: 0, payment_pending: 0 },
        child_lvl_3: { count: 0, total_earned: 0, payment_pending: 0 },
        child_lvl_4: { count: 0, total_earned: 0, payment_pending: 0 }
      });
      setCountLoading(false);

      const message = catchError(e);
      if (message) {
        toast.error(message);
        return;
      }
      toast.error(
        "Ocorreu um erro inesperado ao tentar buscar o total de afiliados"
      );
    }
  };

  const handlePagination = async (
    action: "ROW_CHANGE" | "PAGE_CHANGE",
    newValue: number
  ) => {
    try {
      setLoading(true);
      const filterDate = affiliatedForm.getValues("datePicker")
        ? dateFns.format(affiliatedForm.getValues("datePicker")!, "yyyy-MM-dd")
        : "";
      const level = affiliatedForm.getValues("selectAff")


      if (action === "PAGE_CHANGE") {
        setPagination((prev) => ({ ...prev, page: newValue }));
        const responseAff = await api.get(`/user/getChild/${decodedToken.id}`, {
          params: {
            filterDate,
            level,
            page: newValue,
            pageSize: pagination.pageSize,
          },
        });

        if (responseAff.data && responseAff.data.body) {
          const affiliatesApi: AffiliatedResponse = responseAff.data.body;
          const safeAffiliatesApi = {
            ...affiliatesApi,
            affiliates: affiliatesApi.affiliates || [],
            countChildren: affiliatesApi.countChildren || 0
          };
          setAffiliates(safeAffiliatesApi);
        } else {
          setAffiliates({
            affiliates: [],
            countChildren: 0
          });
        }
      }

      if (action === "ROW_CHANGE") {
        setPagination({ page: 0, pageSize: newValue });
        const responseAff = await api.get(`/user/getChild/${decodedToken.id}`, {
          params: {
            filterDate,
            level,
            page: 0,
            pageSize: newValue,
          },
        });

        if (responseAff.data && responseAff.data.body) {
          const affiliatesApi: AffiliatedResponse = responseAff.data.body;
          const safeAffiliatesApi = {
            ...affiliatesApi,
            affiliates: affiliatesApi.affiliates || [],
            countChildren: affiliatesApi.countChildren || 0
          };
          setAffiliates(safeAffiliatesApi);
        } else {
          setAffiliates({
            affiliates: [],
            countChildren: 0
          });
        }
      }
      setLoading(false);
    } catch (e) {
      // Em caso de erro, garantir que o estado tenha valores padrão
      setAffiliates({
        affiliates: [],
        countChildren: 0
      });

      console.log(e);
      toast.error("Ocorreu um erro ao tentar processar os afiliados");
      setLoading(false);
    }
  };

  // const getAffStats = async () => {
  //   const response = await api.get(`/user/summary-child/${decodedToken.id}`);

  //   const data = response.data.body;

  //   const organizedData = Object.keys(data).map((key) => {
  //     return { name: key, reais: data[key] };
  //   });

  //   setAffStats(organizedData);
  // };


  function formatarMoedaBRL(valor: number | undefined | null): string {
    if (valor === undefined || valor === null || isNaN(Number(valor))) {
      return "Não pagou";
    }

    const numValor = Number(valor);
    const valorFormatado = numValor.toFixed(2).replace(".", ",");
    return `R$ ${valorFormatado.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  }

  useEffect(() => {
    // Garantir que affiliates nunca seja undefined
    setAffiliates({
      affiliates: [],
      countChildren: 0
    });

    getAffiliates();
    getCountAffiliates();
    getGraphData();
    // getAffStats();
    refreshBalance(decodedToken.id);
  }, []);

  return (
    <S.AffiliatedWrapper>
      {isMedia900 ? (
        <S.AffiliatedCopyMobile>
          <h2>Sistema de Afiliados</h2>
          <p>Indique os seus amigos e ganhe dinheiro por cada assinatura.</p>
          <FutButton
            fullWidth
            variant="text"
            onClick={() => {
              //@ts-ignore
              if (navigator.share) {
                navigator.share({
                  title: "Futscore",
                  url: `${window.location.origin}/link/${decodedToken.id}`,
                });
                return;
              }

              navigator.clipboard.writeText(
                `${window.location.origin}/link/${decodedToken.id}`
              );
              toast.success("Copiado com sucesso!");
            }}
          >
            <BiShareAlt /> Compartilhar
          </FutButton>
        </S.AffiliatedCopyMobile>
      ) : (
        <>
          <S.AffiliatedStepsPc>
            <div>
              <span>1</span>
              <p>
                Pegue o seu link personalizado e indique para os seus colegas e
                amigos.
              </p>
            </div>
            <div>
              <span>2</span>
              <p>Receba 20% do valor que o seu amigo pagar na mensalidade.</p>
            </div>
            <div>
              <span>3</span>
              <p>
                Você receberá parte da assinatura de pessoas indicadas pelo seu
                amigo.
              </p>
            </div>
          </S.AffiliatedStepsPc>

          <S.AffiliatedCopyPc>
            <h3>Seu link para afiliação:</h3>
            <div>
              <p>
                {window.location.origin}/link/{decodedToken.id}
              </p>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/link/${decodedToken.id}`
                  );
                  toast.success("Copiado com sucesso!");
                }}
                sx={(props) => ({
                  color: props.palette.fontColor.main,
                  borderColor: props.palette.fontColor.main,
                  background: "rgb(4 231 98)",
                })}
              >
                <MdOutlineCopyAll /> Copiar Link
              </Button>
            </div>
          </S.AffiliatedCopyPc>
        </>
      )}
      <S.YourSalesWrapper>
        <h2>Suas Vendas</h2>
        <Grid
          container
          sx={{
            "@media only screen and (min-width: 1536px)": {
              flexDirection: "row",
            },
            flexDirection: "column",
          }}
        >
          {/* <Grid item xl={3} width="100%">
            <S.MetaAwardContainer>
              <p>Meta para premiação</p>
              <S.AwardBackImage>
                <AiFillTrophy />
              </S.AwardBackImage>
              <div style={{ zIndex: 2 }}>
                <S.AwardValuesContainer>
                  <div>
                    <span>Total Vendido</span>
                    <span>R$ 2.837,00</span>
                  </div>
                  <div>
                    <FaFlagCheckered />
                    <span>Objetivo</span>
                    <span>R$ 8.238,00</span>
                  </div>
                </S.AwardValuesContainer>
                <FutProgress value={55} />
              </div>
            </S.MetaAwardContainer>
          </Grid> */}
          <Grid item xl={6} width="100%">
            {loadingGraph ? (
              <Grid container justifyContent="center">
                <CircularProgress />
              </Grid>
            ) : (
              <S.MonthlySalesContainer>
                <FutDatePicker
                  control={AffStatsForm.control}
                  inputName="dateStats"
                  minDate={new Date("2022-01-02")}
                  maxDate={new Date()}
                  onAccept={() => {
                    getGraphData();
                  }}
                  views={["month", "year"]}
                />
                <p>Vendas por dia</p>
                <ResponsiveContainer
                  width="99%"
                  aspect={isMedia600 ? 1.5 : 3.5}
                >
                  <AreaChart
                    data={affStats}
                    margin={{
                      top: 7,
                      right: 12,
                      left: isMedia600 ? -21 : 0,
                      bottom: 0,
                    }}
                  >
                    <defs>
                      <linearGradient
                        id="graphGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={theme.palette.primary.main}
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor={theme.palette.primary.main}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tickCount={12} interval={0} />
                    <YAxis
                      domain={[0, "dataMax"]}
                      tickFormatter={(tick) => {
                        return tick.toLocaleString();
                      }}
                    />
                    <Tooltip
                      separator="R$"
                      formatter={(value, name) => [
                        `${value.toLocaleString()}`,
                        "",
                      ]}
                      contentStyle={{
                        backgroundColor: theme.palette.backgroundColor.main,
                      }}
                    />
                    <Area
                      dataKey="reais"
                      type="monotone"
                      stroke={theme.palette.primary.main}
                      fillOpacity={1}
                      fill="url(#graphGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </S.MonthlySalesContainer>
            )}
          </Grid>
          <Grid item xl={6} width="100%">
            <S.TotalAffiliatedLevel>
              <p>Total de afiliados por nível</p>
              <S.AffiliatedLevelContainer>
                {countLoading ? (
                  <CircularProgress />
                ) : (
                  <>
                    {Array(3)
                      .fill("")
                      .map((_, i) => {
                        const children = `child_lvl_${i + 1
                          }` as keyof CountUserChildren;
                        return (
                          <S.AffiliatedLevel key={i}>
                            <span>
                              {Array(i + 1)
                                .fill("")
                                .map((_, i) => (
                                  <AiFillStar key={`level${i + 1}-star`} />
                                ))}
                            </span>
                            <MdOutlineGroups />
                            <p>{i + 1}° Nível</p>
                            <div>
                              <span>
                                {countAffiliates && countAffiliates[children] ? countAffiliates[children].count : 0} pessoas
                              </span>
                              <span>
                                {new Intl.NumberFormat("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                }).format(
                                  countAffiliates && countAffiliates[children] ?
                                    countAffiliates[children].total_earned || 0 : 0
                                )}
                              </span>
                              <span></span>
                            </div>
                          </S.AffiliatedLevel>
                        );
                      })}
                  </>
                )}
              </S.AffiliatedLevelContainer>
            </S.TotalAffiliatedLevel>
          </Grid>
        </Grid>
      </S.YourSalesWrapper>

      <S.AffiliatedHistoryCont>
        <h2>Histórico de afiliado</h2>
        {isMedia600 ? (
          <>
            <S.FilterContainer>
              <S.FilterMobileBtn
                onClick={() => setOpenMobileModal((prev) => !prev)}
              >
                <BiFilterAlt />
              </S.FilterMobileBtn>
            </S.FilterContainer>

            <Modal
              open={openMobileModal}
              onClose={() => setOpenMobileModal(false)}
            >
              <S.ModalMobileContainer>
                <S.ModalMobileContent>
                  <h2>
                    Filtros{" "}
                    <span onClick={() => setOpenMobileModal(false)}>X</span>
                  </h2>
                  <p>Data da Venda</p>
                  <FutDatePicker
                    control={affiliatedForm.control}
                    disableFuture
                    minDate={new Date("2022-01-02")}
                    maxDate={new Date()}
                    onAccept={(e: any) =>
                      getAffiliates(dateFns.format(e, "yyyy-MM-dd"))
                    }
                    inputName="datePicker"
                  />

                  <p>Nível do Afiliado</p>
                  <FutSelect
                    control={affiliatedForm.control}
                    inputName="selectAff"
                    data={affiliatedLevelsData}
                    //@ts-ignore
                    onChange={(e) => {
                      affiliatedForm.setValue(
                        "selectAff",
                        e.target.value as number
                      );
                      setPagination((prev) => ({ ...prev, page: 0 }));
                      getAffiliates(undefined, true);
                    }}
                    defaultValue={0}
                  />
                  {/* <S.ModalMobileBtn>
                    <FutButton variant="text" sx={{ px: 3 }}>
                      Limpar Filtro
                    </FutButton>
                    <FutButton sx={{ px: 3 }}>Aplicar Filtro</FutButton>
                  </S.ModalMobileBtn> */}
                </S.ModalMobileContent>
              </S.ModalMobileContainer>
            </Modal>
          </>
        ) : (
          <S.HistoryOptionsCont>
            <S.HistoryButtonsCont>
              <FutDatePicker
                control={affiliatedForm.control}
                disableFuture
                minDate={new Date("2022-01-02")}
                maxDate={new Date()}
                onAccept={(e: any) =>
                  getAffiliates(dateFns.format(e, "yyyy-MM-dd"))
                }
                inputName="datePicker"
                iconOnly={isMedia900}
              />
              <FutSelect
                control={affiliatedForm.control}
                inputName="selectAff"
                data={affiliatedLevelsData}
                //@ts-ignore
                onChange={(e) => {
                  affiliatedForm.setValue(
                    "selectAff",
                    e.target.value as number
                  );
                  setPagination((prev) => ({ ...prev, page: 0 }));
                  getAffiliates(undefined, true);
                }}
                defaultValue={0}
              />
            </S.HistoryButtonsCont>
            {/* <S.HistoryButtonsCont>
              <FutButton variant="text" sx={{ px: 3 }}>
                Limpar Filtro
              </FutButton>
              <FutButton sx={{ px: 3 }}>Aplicar Filtro</FutButton>
            </S.HistoryButtonsCont> */}
          </S.HistoryOptionsCont>
        )}
        {loading ? (
          <Grid container justifyContent="center">
            <CircularProgress />
          </Grid>
        ) : isMedia900 ? (
          <>
            <S.FakeThead>
              <div>Nome do Afiliado</div>
              <div>Data de venda</div>
              <div>Nível do afiliado</div>
            </S.FakeThead>
            {affiliates?.affiliates?.length ? (
              affiliates?.affiliates?.map((el) => (
                <Accordion key={el.id}>
                  <AccordionSummary
                    expandIcon={<BsChevronDown />}
                    sx={{
                      ".MuiAccordionSummary-content": {
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                      },
                      div: {
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  >
                    <div>{el.name || 'Nome não disponível'}</div>
                    <div>
                      {el.payment_date
                        ? dateFns.format(
                          new Date(el.payment_date),
                          "dd/MM/yyyy"
                        )
                        : "Pagamento pendente"}
                    </div>
                    <S.FixStarMobile>
                      {Array(el.level || 1)
                        .fill("")
                        .map((_, i) => (
                          <AiFillStar key={`level${i + 1}-star`} />
                        ))}
                    </S.FixStarMobile>
                  </AccordionSummary>
                  <AccordionDetails>
                    <S.MobileDetailsCont>
                      <div>
                        E-mail
                        <S.DashedContainer>
                          <S.DashedGuide />
                        </S.DashedContainer>
                        {el.email || 'Email não disponível'}
                      </div>
                      <div>
                        Nome do Vendedor
                        <S.DashedContainer>
                          <S.DashedGuide />
                        </S.DashedContainer>
                        {el.parent_user_name || 'Não definido'}
                      </div>
                    </S.MobileDetailsCont>
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <S.NotFoundContainer>
                <span>:(</span>
                <h3>Nenhum afiliado encontrado</h3>
              </S.NotFoundContainer>
            )}
          </>
        ) : affiliates?.affiliates?.length ? (
          <S.AffTable>
            <S.AffTableHead>
              <tr>
                <th>Nome do Afiliado</th>
                <th>E-mail do afiliado</th>
                <th>Data de venda</th>
                <th>Nível do afiliado</th>
                <th>Nome do vendedor</th>
                <th>Valor pago</th>
              </tr>
            </S.AffTableHead>
            <S.AffTableBody>
              {affiliates?.affiliates?.map((el) => (
                <tr key={el.id}>
                  <td>{el.name || 'Nome não disponível'}</td>
                  <td>{el.email || 'Email não disponível'}</td>
                  <td>
                    {el.payment_date
                      ? dateFns.format(new Date(el.payment_date), "dd/MM/yyyy")
                      : "Pagamento pendente"}
                  </td>
                  <S.AffTdStar>
                    {Array(el.level || 1)
                      .fill("")
                      .map((_, i) => (
                        <AiFillStar key={`level${i + 1}-star`} />
                      ))}
                  </S.AffTdStar>
                  <td align="center">{el.parent_user_name || 'Não definido'}</td>
                  <td>{formatarMoedaBRL(el.paid_value)}</td>
                </tr>
              ))}
            </S.AffTableBody>
          </S.AffTable>
        ) : (
          <S.NotFoundContainer>
            <span>:(</span>
            <h3>Nenhum afiliado encontrado</h3>
          </S.NotFoundContainer>
        )}
        <TablePagination
          component="div"
          sx={{ ".MuiTablePagination-selectLabel": { display: "none" } }}
          rowsPerPageOptions={[20, 50, 100, 500]}
          count={affiliates?.countChildren || 0}
          rowsPerPage={pagination.pageSize}
          page={pagination.page}
          onPageChange={(e, newPage) =>
            handlePagination("PAGE_CHANGE", newPage)
          }
          onRowsPerPageChange={(e) => {
            handlePagination("ROW_CHANGE", parseInt(e.target.value));
          }}
        />
      </S.AffiliatedHistoryCont>
    </S.AffiliatedWrapper>
  );
}
